import React, { createContext, useState, useEffect } from 'react';
import { auth, firestore, doc, getDoc, setDoc, onSnapshot } from './firebase-config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [underConstruction, setUnderConstruction] = useState(true);

  const fetchUserData = async (user) => {
    try {
      const userRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userInfo = {
        uid: user.uid,
        email: user.email,
        username: userDoc.exists() ? userDoc.data().username || user.email.split('@')[0] : user.email.split('@')[0],
        role: userDoc.exists() ? userDoc.data().rank || 'User' : 'User',
        rank: userDoc.exists() ? userDoc.data().rank || 'User' : 'User',
      };
      setLoggedInUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    } catch (error) {
      console.error('AuthContext: Error fetching user data:', error);
      const userInfo = {
        uid: user.uid,
        email: user.email,
        username: user.email.split('@')[0],
        role: 'User',
        rank: 'User',
      };
      setLoggedInUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setLoggedInUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('AuthContext: Error parsing stored user:', error);
        localStorage.removeItem('user');
      }
    }

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user);
      } else {
        setLoggedInUser(null);
        localStorage.removeItem('user');
        setLoading(false); // Set loading false if no user
      }
    });

    // Real-time listener for underConstruction setting
    const settingsRef = doc(firestore, 'siteSettings', 'underConstruction');
    const unsubscribeSettings = onSnapshot(settingsRef, (doc) => {
      if (doc.exists()) {
        setUnderConstruction(doc.data().isEnabled);
      } else {
        setUnderConstruction(true); // Default to enabled
      }
      setLoading(false); // Set loading false after fetching
    }, (error) => {
      console.error('AuthContext: Error listening to under construction setting:', error);
      setUnderConstruction(true);
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeSettings();
    };
  }, []);

  const loginUser = (userData) => {
    const userInfo = {
      uid: userData.uid || userData.email,
      email: userData.email,
      username: userData.username || userData.email.split('@')[0],
      role: userData.rank || 'User',
      rank: userData.rank || 'User',
    };
    setLoggedInUser(userInfo);
    localStorage.setItem('user', JSON.stringify(userInfo));
  };

  const logoutUser = () => {
    auth.signOut().then(() => {
      setLoggedInUser(null);
      localStorage.removeItem('user');
    }).catch((error) => {
      console.error('AuthContext: Error signing out:', error);
    });
  };

  const toggleUnderConstruction = async (isEnabled) => {
    try {
      const settingsRef = doc(firestore, 'siteSettings', 'underConstruction');
      await setDoc(settingsRef, { isEnabled });
      setUnderConstruction(isEnabled);
    } catch (error) {
      console.error('AuthContext: Error updating under construction setting:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ loggedInUser, loginUser, logoutUser, loading, underConstruction, toggleUnderConstruction }}>
      {children}
    </AuthContext.Provider>
  );
};